import { Component, EventEmitter, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { UserSandbox } from "../../../shared/sandbox/providers/user.sandbox";
import {
  SearchCountryField,
  TooltipLabel,
  CountryISO,
  PhoneNumberFormat,
} from "ngx-intl-tel-input";
import { practitionersH1, practitionersH2 } from './practitioners';
import { GlobalSandbox } from "../../../shared/sandbox/providers/global.sandbox";
import { take } from "rxjs/operators";
import { DateFormatUtils } from "../../../shared/utils/date-format.utils";
import { Select } from "@ngxs/store";
import { BioSealState } from "../../../shared/store/state/bioseal.state";
import { Observable } from "rxjs";
import { BioSealStateModel } from "../../../shared/model/bioseal.model";


@Component({
  selector: "id3-vaccination-user-form",
  templateUrl: "./vaccination-user-form.component.html",
  styleUrls: ["./vaccination-user-form.component.css"],
  providers: [UserSandbox],
  encapsulation: ViewEncapsulation.None,
})
export class VaccinationUserFormComponent {
  PersonalForm;
  DocumentForm;
  PractitionerForm;
  VaccinationForm;
  defaultDate;
  separateDialCode = false;
  practitionersToShow = practitionersH1;
  selectedPractitioner: any;
  selectedHospital: string;

  SearchCountryField = SearchCountryField;
  TooltipLabel = TooltipLabel;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];

  @Select(BioSealState.isPersonalDataLoaded) personalData: Observable<BioSealStateModel>;
  @Select(BioSealState.isDocumentDataLoaded) documentData: Observable<BioSealStateModel>;
  @Select(BioSealState.isPractitionerDataLoaded) practitionerData: Observable<BioSealStateModel>;
  @Select(BioSealState.isPersonalFormSubmitted) personalSubmitted: Observable<BioSealStateModel>;
  @Select(BioSealState.isDocumentFormSubmitted) documentSubmitted: Observable<BioSealStateModel>;
  @Select(BioSealState.isPractitionerFormSubmitted) practitionerSubmitted: Observable<BioSealStateModel>;

  @Output() nextEvent = new EventEmitter<boolean>();
  @Output() previousEvent = new EventEmitter<boolean>();

  constructor(
    public userSandbox: UserSandbox,
    private formBuilder: FormBuilder,
    public globalSandbox: GlobalSandbox
  ) {
    this.PersonalForm = this.formBuilder.group({
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      dob: ["", [Validators.required]],
      pob: [null],
      email: [
        "",
        [Validators.required, Validators.email, Validators.maxLength(50)],
      ],
      confirmEmail: [
        "",
        [
          Validators.required,
          Validators.email,
          this.userSandbox.EmailValidator("email"),
          Validators.maxLength(50),
        ],
      ],
      phoneNumber: [undefined, Validators.required],
      idNumber: [null],
    });
    this.PersonalForm.valueChanges.subscribe(() => {
      this.userSandbox.onPersonalFormValid({
        data: this.PersonalForm.value,
        validation: this.PersonalForm.valid,
      });
    });
    this.DocumentForm = this.formBuilder.group({
      certificateNumber: [null, Validators.required],
      vaccinationName: [null, Validators.required],
      dov: [DateFormatUtils.todayToStringDate(), Validators.required],
      batchNumber: [null],
    });
    this.DocumentForm.valueChanges.subscribe(() => {
      this.userSandbox.onDocumentFormValid({
        data: this.DocumentForm.value,
        validation: this.DocumentForm.valid,
      });
    });
    this.defaultDate = new Date();
    this.PractitionerForm = this.formBuilder.group({
      vaccinationPlace: [null],
      practitioner: [null, Validators.required],
      lastName: [null, Validators.required],
      firstName: [null, Validators.required],
      function: [null],
      phoneNumber: [null],
      email: [
        "",
        [Validators.email, Validators.required, Validators.maxLength(50)],
      ],
    });

  }

  public selectVaccinationCenter() {
    this.selectedHospital = this.PractitionerForm.get('vaccinationPlace').value;
    this.practitionersToShow = this.selectedHospital === "CHU Grenoble Alpes" ? practitionersH1 : practitionersH2;

    if (this.PractitionerForm.get('practitioner').value !== null) {
      this.PractitionerForm = this.formBuilder.group({
        vaccinationPlace: [null],
        practitioner: [null, Validators.required],
        lastName: [null, Validators.required],
        firstName: [null, Validators.required],
        function: [null],
        phoneNumber: [null],
        email: [
          "",
          [Validators.email, Validators.required, Validators.maxLength(50)],
        ],
      });
    }

  }

  public autoFill() {

    this.selectedPractitioner = this.PractitionerForm.get('practitioner').value;
    this.globalSandbox.translate.get("practitionerTitleValue").pipe(take(1)).subscribe(
      (data) => {
        this.PractitionerForm = this.formBuilder.group({
          vaccinationPlace: [this.selectedHospital],
          practitioner: [this.selectedPractitioner, Validators.required],
          lastName: [this.selectedPractitioner.lastName, Validators.required],
          firstName: [this.selectedPractitioner.firstName, Validators.required],
          function: [data],
          phoneNumber: [this.selectedPractitioner.tel],
          email: [
            this.selectedPractitioner.email,
            [Validators.email, Validators.required, Validators.maxLength(50)],
          ],
        });
      }
    )

    this.PractitionerForm.valueChanges.subscribe(() => {
      this.userSandbox.onPractitionerFormValid({
        data: this.PractitionerForm.value,
        validation: this.PractitionerForm.valid,
      });
    });
  }

  onPersonalSubmit() {
    this.personalData.pipe(take(1)).subscribe((loaded) => {
      loaded ? this.nextEvent.emit() : this.userSandbox.onPersonalFormSubmitted();
    });
  }

  onPractitionerSubmit() {
    this.practitionerData.pipe(take(1)).subscribe((loaded) => {
      loaded ? this.nextEvent.emit() : this.userSandbox.onPractitionerFormSubmitted();
    });
  }

  onDocumentSubmit() {
    this.documentData.pipe(take(1)).subscribe((loaded) => {
      loaded ? this.nextEvent.emit() : this.userSandbox.onDocumentFormSubmitted();
    });
  }

  previous() {
    this.previousEvent.emit();
  }

}
